/**
 * CopyProvider / useCopy — the register, resolved once for the tree.
 *
 * Mirrors `packages/ui/src/theme/CopyProvider.tsx`'s contract exactly (`t`, `term`,
 * `register`, `literal`) so the convention CLAUDE.md states — "Components read...
 * copy from `useCopy()`" — holds in this app too, without depending on `@carn/ui`
 * (out of scope here; see the note in `ThemeProvider.tsx`).
 *
 * Same rule as the mobile app: no escape hatch. A component gets `t` and `term`, and
 * if a string is missing it goes into `packages/copy/src/catalogue.ts` with both
 * registers — never inlined (CLAUDE.md, design-direction §1).
 */
import React, { createContext, useContext, useMemo } from 'react';
import { makeTranslator, pick, registerFor, terms, type Register, type Translator } from '@carn/copy';
import { useTheme } from './ThemeProvider';

export type TermKey = keyof typeof terms;

export type CopyApi = {
  t: Translator;
  term: (key: TermKey) => string;
  register: Register;
  literal: boolean;
};

const CopyContext = createContext<CopyApi | null>(null);

export function CopyProvider({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  // Same double-entry as the mobile app: the explicit literal setting, or voice
  // level 0 (the dial's own "plain words" promise) — either one is plain words.
  const register: Register = registerFor(theme.literal || theme.levels.voice === 0);

  const value = useMemo<CopyApi>(() => {
    const t = makeTranslator(register);
    return {
      t,
      term: (key) => pick(terms[key], register),
      register,
      literal: register === 'literal',
    };
  }, [register]);

  return <CopyContext.Provider value={value}>{children}</CopyContext.Provider>;
}

export function useCopy(): CopyApi {
  const ctx = useContext(CopyContext);
  if (!ctx) {
    throw new Error(
      'useCopy() outside a CopyProvider. Components must not inline user-visible ' +
        'strings (design-direction §1), so there is no fallback register.',
    );
  }
  return ctx;
}
